import { z } from "zod";
import { dashboardRangeSchema } from "./dashboard";

export const salesTrafficUnavailableReasonSchema = z.enum([
  "scope_incomplete",
  "missing_sales",
  "missing_sp_spend",
  "currency_mismatch",
  "timezone_unverified",
  "zero_sales",
  "stale_sales",
]);
export const salesTrafficPeriodSchema = z.object({
  startDate: z.string().date(),
  endDate: z.string().date(),
  totalSales: z.number().nonnegative().nullable(),
  spSpend: z.number().nonnegative().nullable(),
  /** Fraction: 0.1 means 10%; the numerator covers Sponsored Products only. */
  tacos: z.number().nonnegative().nullable(),
  /** Matched shop/date amounts; sum these before calculating week/month TACOS. */
  tacosCoverage: z
    .object({
      sales: z.number().nonnegative(),
      spSpend: z.number().nonnegative(),
      coveredShopDays: z.number().int().nonnegative(),
    })
    .nullable()
    .optional(),
  provisionalShopDays: z.number().int().nonnegative().optional(),
  unavailableReasons: z.array(salesTrafficUnavailableReasonSchema),
  expectedShopDays: z.number().int().nonnegative(),
  salesCoveredShopDays: z.number().int().nonnegative(),
  spendCoveredShopDays: z.number().int().nonnegative(),
  /**
   * Amazon "Total Order Items" summed over the shop-days in
   * `ordersCoveredShopDays`; taken from the same source row as each day's
   * sales. Null when no shop-day in the period has order data.
   * The API always sends both order fields; they are optional only so existing
   * client fixtures keep compiling, as with `provisionalShopDays`.
   */
  totalOrders: z.number().int().nonnegative().nullable().optional(),
  /** Shop-days with order data; compare with `expectedShopDays` for completeness. */
  ordersCoveredShopDays: z.number().int().nonnegative().optional(),
  stale: z.boolean(),
});
export type SalesTrafficPeriod = z.infer<typeof salesTrafficPeriodSchema>;
export const salesTrafficSyncStatusSchema = z.object({
  status: z.enum(["queued", "processing", "success", "failed", "cancelled"]),
  errorCode: z.string().nullable(),
  createdAt: z.string().datetime(),
  finishedAt: z.string().datetime().nullable(),
});
export const salesTrafficResponseSchema = z.object({
  connected: z.boolean(),
  range: dashboardRangeSchema,
  salesSource: z.enum(["GET_SALES_AND_TRAFFIC_REPORT", "reports_and_sales_api"]),
  salesBasis: z.literal("ordered_product_sales"),
  adSpendScope: z.literal("sponsored_products"),
  currency: z.string().nullable(),
  currencyApproximate: z.boolean(),
  excludedShops: z.number().int().nonnegative(),
  dataThrough: z.string().date().nullable(),
  current: salesTrafficPeriodSchema,
  previous: salesTrafficPeriodSchema,
  trend: z.array(salesTrafficPeriodSchema.extend({ date: z.string().date() })),
  shops: z.array(
    z.object({
      shopId: z.string(),
      connected: z.boolean(),
      availability: z.enum([
        "connected",
        "reauthorization_required",
        "not_connected",
        "unsupported",
      ]),
      timeZone: z.string().nullable(),
      timezoneAlignment: z.enum(["verified", "configured", "unverified"]),
      latestSync: salesTrafficSyncStatusSchema.nullable(),
      lastSyncedAt: z.string().datetime().nullable(),
    }),
  ),
});
export type SalesTrafficResponse = z.infer<typeof salesTrafficResponseSchema>;

export const salesTrafficSyncRequestSchema = z
  .object({
    startDate: z.string().date(),
    endDate: z.string().date(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const start = Date.parse(value.startDate);
    const end = Date.parse(value.endDate);
    const now = new Date();
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    const earliest = new Date(today);
    earliest.setUTCFullYear(earliest.getUTCFullYear() - 2);
    if (end < start || (end - start) / 86_400_000 + 1 > 60) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: "Sales sync window must contain 1 to 60 days",
      });
    }
    if (end >= today.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["endDate"],
        message: "Sales sync must end before today",
      });
    }
    if (start < earliest.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["startDate"],
        message: "Sales sync supports at most two years of history",
      });
    }
  });
export type SalesTrafficSyncRequest = z.infer<typeof salesTrafficSyncRequestSchema>;
